import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {number} */
  #currentIndex = 0;

  get #gallery() {
    return this.querySelector('.dialog-zoomed-gallery');
  }

  connectedCallback() {
    super.connectedCallback();
    const gallery = this.#gallery;
    if (gallery) {
      gallery.addEventListener('scroll', this.#handleGalleryScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const gallery = this.#gallery;
    if (gallery) {
      gallery.removeEventListener('scroll', this.#handleGalleryScroll);
    }
  }

  /**
   * Opens the zoom dialog.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#currentIndex = index;

    const open = () => {
      // Set scroll position BEFORE showing the dialog so the first paint is correct
      const gallery = this.#gallery;
      if (gallery) {
        gallery.style.scrollBehavior = 'auto';
        gallery.scrollLeft = gallery.offsetWidth * index;
        gallery.style.scrollBehavior = '';
      }
      dialog.showModal();
      targetThumbnail?.scrollIntoView({ behavior: 'instant' });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    // Skip view transition on mobile — it causes more flicker than it prevents
    const isMobile = window.innerWidth < 750;
    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage || isMobile) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');
    this.#syncThumbnail(index, 'instant');
  }

  /**
   * Loads a high-resolution image for a specific media container.
   * @param {HTMLElement} mediaContainer - The media container element.
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.onload = () => {
      // Fade the new image in over the existing one instead of replacing it
      // This prevents the white flash caused by removing the element from the DOM
      image.style.transition = 'opacity 0s';
      image.style.opacity = '1';
      image.src = highResolutionUrl;
      image.style.transition = '';
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles scroll on the horizontal gallery.
   * Uses debounce so it fires after snapping has settled.
   */
  #handleGalleryScroll = debounce(() => {
    const gallery = this.#gallery;
    const { media } = this.refs;
    if (!gallery || !media.length) return;

    const width = gallery.offsetWidth;
    if (!width) return;

    const count = media.length;
    const index = Math.round(gallery.scrollLeft / width);
    const clamped = Math.max(0, Math.min(index, count - 1));

    const atEnd = clamped === count - 1;
    const atStart = clamped === 0;
    const scrollingRight = clamped > this.#currentIndex;
    const scrollingLeft = clamped < this.#currentIndex;

    if (atEnd && scrollingRight && this.#currentIndex === count - 1) {
      gallery.scrollLeft = 0;
      this.#currentIndex = 0;
      this.#syncThumbnail(0, 'instant');
      this.dispatchEvent(new ZoomMediaSelectedEvent(0));
      return;
    }

    if (atStart && scrollingLeft && this.#currentIndex === 0) {
      gallery.scrollLeft = width * (count - 1);
      this.#currentIndex = count - 1;
      this.#syncThumbnail(count - 1, 'instant');
      this.dispatchEvent(new ZoomMediaSelectedEvent(count - 1));
      return;
    }

    if (clamped === this.#currentIndex) return;
    this.#currentIndex = clamped;

    this.#syncThumbnail(clamped, 'smooth');
    if (media[clamped]) this.#loadHighResolutionImage(media[clamped]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(clamped));
  }, 80);

  /**
   * Updates which thumbnail is highlighted and scrolls it into view.
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #syncThumbnail(index, behavior = 'smooth') {
    const { thumbnails } = this.refs;
    if (!thumbnails) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    const targetThumbnail = thumbnails.children[index];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = this.#currentIndex;
    const mostVisibleElement = media[activeIndex];
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide || !mostVisibleElement) return this.closeDialog();

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;
    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index
   * @param {Object} options
   * @param {ScrollBehavior} options.behavior
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media } = this.refs;

    this.#currentIndex = index;
    this.#syncThumbnail(index, options.behavior);

    const gallery = this.#gallery;
    if (gallery) {
      gallery.scrollLeft = gallery.offsetWidth * index;
    }

    const targetImage = media[index];
    if (targetImage) {
      this.#loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}